<div class="account-page">

  <!-- Loading skeleton -->
  <div *ngIf="!user && loadingAccount" class="loading-skeleton">
    <div class="skeleton profile-header-skeleton">
      <div class="skeleton-avatar"></div>
      <div class="skeleton-lines">
        <div class="skeleton-line skeleton-line-w60"></div>
        <div class="skeleton-line skeleton-line-w40"></div>
      </div>
    </div>
    <div class="skeleton-grid">
      <div class="skeleton-card"></div>
      <div class="skeleton-card"></div>
    </div>
  </div>

  <!-- Profile header with avatar -->
  <div class="card profile-header" *ngIf="user">
    <div class="profile-avatar-wrapper">
      <div class="profile-avatar-container" (click)="avatarInput.click()">
        <p-avatar
          [image]="avatarUrl || undefined"
          [label]="!avatarUrl ? initials : ''"
          shape="circle"
          size="xlarge"
          styleClass="profile-avatar-png"
        ></p-avatar>
        <div class="avatar-upload-overlay" *ngIf="!uploadingAvatar">
          <i class="pi pi-camera"></i>
        </div>
        <div class="avatar-spinner" *ngIf="uploadingAvatar">
          <i class="pi pi-spin pi-spinner"></i>
        </div>
      </div>
      <input
        type="file"
        #avatarInput
        accept="image/png,image/jpeg,image/webp"
        (change)="onAvatarSelected($event)"
        style="display:none"
      />
      <button
        class="btn btn-outline btn-sm avatar-change-btn"
        (click)="avatarInput.click()"
        [disabled]="uploadingAvatar"
      >
        {{ uploadingAvatar ? 'Uploading...' : 'Change Photo' }}
      </button>
    </div>

    <div class="profile-info">
      <h2>{{ fullName }}</h2>
      <p class="profile-email">{{ email }}</p>
      <div class="profile-roles" *ngIf="roles.length">
        <span class="role-tag" *ngFor="let role of roles">{{ role }}</span>
      </div>
    </div>
  </div>

  <div class="account-grid" *ngIf="user">

    <!-- Contact info -->
    <div class="card account-section">
      <h3><i class="pi pi-id-card"></i> Contact Information</h3>
      <div class="info-row">
        <span class="info-label">Email</span>
        <span class="info-value">{{ email }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Phone</span>
        <span class="info-value">{{ phone || 'Not set' }}</span>
      </div>

      <div class="section-actions">
        <button
          class="btn btn-outline btn-sm"
          (click)="startEditProfile()"
          [disabled]="loadingAccount"
        >
          <i class="pi pi-pencil"></i>
          Edit Profile
        </button>
      </div>
    </div>

    <!-- Security (includes 2FA) -->
    <div class="card account-section">
      <h3><i class="pi pi-shield"></i> Security</h3>

      <div class="info-row">
        <span class="info-label">Password</span>
        <span class="info-value">
          <span class="badge badge-success">Set</span>
        </span>
      </div>

      <div class="info-row">
        <span class="info-label">Two-Factor Authentication</span>
        <span class="info-value">
          <span *ngIf="loadingAccount" class="badge badge-muted">Loading...</span>
          <span *ngIf="!loadingAccount && twoFactorEnabled" class="badge badge-success">
            <i class="pi pi-check-circle"></i> Enabled
          </span>
          <span *ngIf="!loadingAccount && !twoFactorEnabled" class="badge badge-warning">
            Not enabled
          </span>
        </span>
      </div>

      <!-- 2FA setup (legacy, only when FA isn't managing auth at all) -->
      <div class="two-factor-inline" *ngIf="!faAccount && !loadingAccount && user">
        <app-two-factor-setup [user]="user"></app-two-factor-setup>
      </div>

      <div class="section-actions">
        <button
          class="btn btn-outline btn-sm"
          (click)="startChangePassword()"
          [disabled]="loadingAccount"
        >
          <i class="pi pi-lock"></i>
          Change Password
        </button>
        <button
          *ngIf="!twoFactorEnabled"
          class="btn btn-outline btn-sm"
          (click)="startEnableTwoFactor()"
          [disabled]="loadingAccount || enrollingTwoFactor"
        >
          <i class="pi pi-shield"></i>
          {{ enrollingTwoFactor ? 'Starting...' : 'Enable 2FA' }}
        </button>
        <button
          *ngIf="twoFactorEnabled"
          class="btn btn-outline btn-sm"
          (click)="disableTwoFactor()"
          [disabled]="loadingAccount"
        >
          <i class="pi pi-shield"></i>
          Disable 2FA
        </button>
      </div>
    </div>

    <!-- Passkeys -->
    <div class="card account-section">
      <h3><i class="pi pi-key"></i> Passkeys</h3>
      <p class="section-hint">Passkeys let you sign in with Face ID, Touch ID, or a hardware security key.</p>
      <div *ngIf="loadingPasskeys" class="info-row">
        <span class="info-label">Loading...</span>
      </div>
      <div *ngIf="!loadingPasskeys && passkeys.length === 0" class="info-row">
        <span class="info-value muted">No passkeys registered.</span>
      </div>
      <div *ngFor="let pk of passkeys" class="info-row passkey-row">
        <span class="info-label">
          <i class="pi pi-key"></i>
          {{ pk.name || 'Unnamed passkey' }}
        </span>
        <button
          class="btn btn-outline btn-sm btn-danger"
          (click)="deletePasskey(pk.id)"
        >
          <i class="pi pi-trash"></i>
          Remove
        </button>
      </div>
    </div>

    <!-- Sessions -->
    <div class="card account-section">
      <h3><i class="pi pi-desktop"></i> Active Sessions</h3>
      <p class="section-hint">
        These are the devices and browsers where you're signed in.
        <span *ngIf="!recentlyAuthed">Re-authenticate to revoke sessions.</span>
      </p>
      <div *ngIf="loadingSessions" class="info-row">
        <span class="info-label">Loading...</span>
      </div>
      <div *ngIf="!loadingSessions && sessions.length === 0" class="info-row">
        <span class="info-value muted">No active sessions found.</span>
      </div>
      <div *ngFor="let s of sessions" class="info-row passkey-row">
        <span class="info-label">
          <i class="pi pi-desktop"></i>
          {{ s.startInstant ? (s.startInstant | date:'medium') : 'Unknown start' }}
        </span>
        <button
          class="btn btn-outline btn-sm btn-danger"
          (click)="revokeSession(s.id)"
          [disabled]="!recentlyAuthed"
        >
          <i class="pi pi-trash"></i>
          Revoke
        </button>
      </div>
    </div>

    <ng-container *hasPermission="['mcp.access']">
      <div class="card account-section mcp-section" *ngIf="user">
        <h3><i class="pi pi-share-alt"></i> Connected Applications</h3>
        <app-mcp-connections [user]="user"></app-mcp-connections>
      </div>
    </ng-container>

  </div>

  <!-- Edit Profile Dialog -->
  <p-dialog
    [(visible)]="editingProfile"
    header="Edit Profile"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    styleClass="account-dialog"
  >
    <div class="dialog-form">
      <div class="form-field">
        <label for="firstName">First Name</label>
        <input
          pInputText
          id="firstName"
          type="text"
          [(ngModel)]="profileForm.firstName"
        />
      </div>
      <div class="form-field">
        <label for="lastName">Last Name</label>
        <input
          pInputText
          id="lastName"
          type="text"
          [(ngModel)]="profileForm.lastName"
        />
      </div>
      <div class="form-field">
        <label for="mobilePhone">Phone Number</label>
        <input
          pInputText
          id="mobilePhone"
          type="tel"
          [(ngModel)]="profileForm.mobilePhone"
        />
      </div>
    </div>
    <ng-template pTemplate="footer">
      <button class="btn btn-outline btn-sm" (click)="cancelEditProfile()">Cancel</button>
      <button class="btn btn-primary btn-sm" (click)="saveProfile()" [disabled]="savingProfile">
        {{ savingProfile ? 'Saving...' : 'Save' }}
      </button>
    </ng-template>
  </p-dialog>

  <!-- Change Password Dialog -->
  <p-dialog
    [(visible)]="changingPassword"
    header="Change Password"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    styleClass="account-dialog"
  >
    <div class="dialog-form">
      <div class="form-field">
        <label for="currentPassword">Current Password</label>
        <input
          pInputText
          id="currentPassword"
          type="password"
          [(ngModel)]="passwordForm.currentPassword"
        />
      </div>
      <div class="form-field">
        <label for="newPassword">New Password</label>
        <input
          pInputText
          id="newPassword"
          type="password"
          [(ngModel)]="passwordForm.newPassword"
        />
      </div>
      <div class="form-field">
        <label for="confirmPassword">Confirm New Password</label>
        <input
          pInputText
          id="confirmPassword"
          type="password"
          [(ngModel)]="passwordForm.confirmPassword"
        />
      </div>
    </div>
    <ng-template pTemplate="footer">
      <button class="btn btn-outline btn-sm" (click)="cancelChangePassword()">Cancel</button>
      <button class="btn btn-primary btn-sm" (click)="savePassword()" [disabled]="savingPassword">
        {{ savingPassword ? 'Saving...' : 'Change Password' }}
      </button>
    </ng-template>
  </p-dialog>

  <!-- 2FA Enrollment Dialog -->
  <p-dialog
    [(visible)]="enableTwoFactorDialog"
    header="Enable Two-Factor Authentication"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    styleClass="account-dialog"
  >
    <div class="dialog-form">
      <p *ngIf="twoFactorQrDataUrl" class="qr-section">
        <img [src]="twoFactorQrDataUrl" alt="QR Code" class="qr-code" />
      </p>
      <p class="form-hint">Scan the QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.), then enter the 6-digit code below.</p>
      <p *ngIf="twoFactorSecret" class="form-hint">
        Can't scan? Enter this secret manually: <code>{{ twoFactorSecret }}</code>
      </p>
      <div class="form-field">
        <label for="twoFactorCode">Verification Code</label>
        <input
          pInputText
          id="twoFactorCode"
          type="text"
          maxlength="6"
          placeholder="123456"
          [(ngModel)]="twoFactorCode"
        />
      </div>
    </div>
    <ng-template pTemplate="footer">
      <button class="btn btn-outline btn-sm" (click)="cancelTwoFactor()">Cancel</button>
      <button class="btn btn-primary btn-sm" (click)="confirmTwoFactor()" [disabled]="enrollingTwoFactor">
        {{ enrollingTwoFactor ? 'Verifying...' : 'Verify & Enable' }}
      </button>
    </ng-template>
  </p-dialog>

  <!-- Re-authentication dialog -->
  <p-dialog
    [(visible)]="showReAuthDialog"
    [modal]="true"
    [style]="{ width: '400px' }"
    header="Re-authenticate"
    [draggable]="false"
    [resizable]="false"
  >
    <div class="re-auth-content">
      <p class="form-hint">
        For your security, please re-enter your password to continue.
        This unlocks sensitive actions for 15 minutes.
      </p>
      <div class="form-field">
        <label for="reAuthPassword">Password</label>
        <input
          pInputText
          id="reAuthPassword"
          type="password"
          autocomplete="nope"
          [(ngModel)]="reAuthPassword"
          (keyup.enter)="confirmReAuth()"
        />
      </div>
    </div>
    <ng-template pTemplate="footer">
      <button class="btn btn-outline btn-sm" (click)="cancelReAuth()">Cancel</button>
      <button class="btn btn-primary btn-sm" (click)="confirmReAuth()" [disabled]="reAuthLoading || !reAuthPassword">
        {{ reAuthLoading ? 'Verifying...' : 'Verify' }}
      </button>
    </ng-template>
  </p-dialog>

  <!-- Legal -->
  <div class="legal-footer">
    <span>&copy; {{ year }} Karve Information Technology Ltd. All rights reserved.</span>
    <span class="legal-trademark">Move Right&trade; and the Move Right logo are trademarks of Karve IT Ltd. A Mobio Brands (TSX.V: MBO) company.</span>
    <span class="legal-links">
      <a href="https://moveright.app/terms" target="_blank" rel="noopener">Terms of Service</a>
      &middot;
      <a href="https://moveright.app/privacy" target="_blank" rel="noopener">Privacy Policy</a>
    </span>
  </div>

</div>